import { createFileRoute } from '@tanstack/react-router';
import {
  Avatar, Button, Card, Center, Group, Loader, Select, Stack, Text, Textarea, TextInput, Title,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { IconCamera, IconCheck, IconPhoto } from '@tabler/icons-react';
import { PageLoader } from '@/shared/components/LoadingState';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { useEffect, useState } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import * as userApi from '@/features/users/api';
import { useDirtyFormGuard } from '@/shared/hooks/useDirtyFormGuard';
import { MaePhotoInput } from '@/shared/components/MaePhotoInput';
import { nativeCamera } from '@/shared/lib/camera';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';

export const Route = createFileRoute('/_app/settings/profile')({ component: ProfilePage });

const PHONE_RE = /^\+?[\d\s-]{5,20}$/;
const PHONE_MSG = 'Solo cifre, +, spazi e trattini';

/** Campo opzionale: vuoto OK, ma se compilato deve rispettare lo schema. */
const optional = (schema: z.ZodTypeAny) => schema.or(z.literal(''));

const accountSchema = z.object({
  email: z.string().email('Email non valida'),
  phoneNumber: optional(z.string().regex(PHONE_RE, PHONE_MSG)),
});

const profileSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  nickName: z.string(),
  sex: z.string(),
  taxId: optional(z.string().regex(/^[A-Za-z0-9]{11,16}$/, 'Codice fiscale non valido')),
  birthDate: z.string().refine((v) => !v || new Date(v) <= new Date(), 'La data non può essere nel futuro'),
  fixedPhone: optional(z.string().regex(PHONE_RE, PHONE_MSG)),
  mobilePhone: optional(z.string().regex(PHONE_RE, PHONE_MSG)),
  contactEmail: optional(z.string().email('Email non valida')),
  residenceAddress: z.string(),
  residenceHouseNumber: z.string(),
  residenceCity: z.string(),
  residenceProvince: optional(z.string().max(50, 'Massimo 50 caratteri')),
  residenceZIP: optional(z.string().regex(/^\d{4,10}$/, 'CAP non valido (solo cifre)')),
  residenceState: z.string(),
  occupation: z.string(),
  description: z.string(),
  userLang: z.string(),
});

function ProfilePage() {
  const { decoded, user } = useAuth();
  const userId = decoded?.sub ?? user?.id ?? '';
  const qc = useQueryClient();
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['me-with-profile', userId],
    queryFn: () => userApi.fetchUserWithProfile(userId),
    enabled: Boolean(userId),
  });
  const me = data?.user;
  const profile = data?.profile;

  const accountForm = useForm({
    mode: 'controlled',
    initialValues: { email: '', phoneNumber: '' },
    validate: schemaResolver(accountSchema, { sync: true }),
  });

  const profileForm = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: '', lastName: '', nickName: '',
      sex: '', taxId: '', birthDate: '',
      fixedPhone: '', mobilePhone: '', contactEmail: '',
      residenceAddress: '', residenceHouseNumber: '', residenceCity: '',
      residenceProvince: '', residenceZIP: '', residenceState: '',
      occupation: '', description: '', userLang: 'it',
    },
    validate: schemaResolver(profileSchema, { sync: true }),
  });

  // Avvisa su chiusura tab / navigazione con modifiche non salvate
  useDirtyFormGuard(accountForm.isDirty() || profileForm.isDirty());

  useEffect(() => {
    if (me) {
      accountForm.setValues({ email: me.email ?? '', phoneNumber: me.phoneNumber ?? '' });
      accountForm.resetDirty({ email: me.email ?? '', phoneNumber: me.phoneNumber ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  useEffect(() => {
    if (profile) {
      const values = {
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        nickName: profile.nickName ?? '',
        sex: profile.sex ?? '',
        taxId: profile.taxId ?? '',
        birthDate: profile.birthDate ? profile.birthDate.slice(0, 10) : '',
        fixedPhone: profile.fixedPhone ?? '',
        mobilePhone: profile.mobilePhone ?? '',
        contactEmail: profile.contactEmail ?? '',
        residenceAddress: profile.residenceAddress ?? '',
        residenceHouseNumber: profile.residenceHouseNumber ?? '',
        residenceCity: profile.residenceCity ?? '',
        residenceProvince: profile.residenceProvince ?? '',
        residenceZIP: profile.residenceZIP ?? '',
        residenceState: profile.residenceState ?? '',
        occupation: profile.occupation ?? '',
        description: profile.description ?? '',
        userLang: profile.userLang ?? 'it',
      };
      profileForm.setValues(values);
      profileForm.resetDirty(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const saveAccount = useMutation({
    mutationFn: (v: typeof accountForm.values) => userApi.updateMe(userId, v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Account aggiornato' });
      accountForm.resetDirty();
      qc.invalidateQueries({ queryKey: ['me-with-profile', userId] });
    },
    onError: (err) => notifyError(err, 'Errore account'),
  });

  const saveProfile = useMutation({
    mutationFn: (v: typeof profileForm.values) => {
      const attrs = { ...v, birthDate: v.birthDate || undefined };
      if (profile?.id) return userApi.updateUserProfile(profile.id, attrs);
      return userApi.createUserProfile(userId, { ...attrs, contactEmail: attrs.contactEmail || me?.email });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Profilo aggiornato' });
      profileForm.resetDirty();
      qc.invalidateQueries({ queryKey: ['me-with-profile', userId] });
    },
    onError: (err) => notifyError(err, 'Errore profilo'),
  });

  /**
   * Foto profilo attuale: il BE non ha una rotta binaria, quindi il data-URL arriva dal
   * single-GET sul media-file collegato (`user-profiles.profileImage`). Senza questa query
   * l'`Avatar` non aveva alcun `src` e mostrava sempre l'iniziale, anche dopo l'upload.
   */
  const avatarImageId = profile?.profileImage?.id ?? profile?.profileImageId ?? null;
  const avatarQ = useQuery({
    queryKey: ['profile-image', avatarImageId],
    enabled: Boolean(avatarImageId),
    staleTime: 5 * 60_000,
    queryFn: () => userApi.fetchProfileImage(avatarImageId!, 'md'),
  });

  /**
   * Conferma prima di caricare: il file scelto resta in attesa con un'anteprima locale
   * (`URL.createObjectURL`) finché l'utente non conferma. Prima il `FileButton` partiva
   * direttamente con l'upload, senza mostrare cosa stava caricando.
   */
  /* Lo scatto si offre dove ha senso: nell'app nativa (c’è il plugin) e da telefono sul web,
     dove l'`<input capture>` apre la fotocamera di sistema. Su desktop resta il solo selettore
     file: un pulsante «scatta» aprirebbe la webcam, che per una foto profilo non è quello che
     si cerca. */
  const isMobile = useIsMobileShell();
  const withCamera = isMobile || nativeCamera();

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const pickAvatar = (file: File | null) => {
    if (!file) return;
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
  };
  const clearPending = () => {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(null);
    setPendingUrl(null);
  };
  // L'object URL va revocato all'unmount, altrimenti resta allocato.
  useEffect(() => () => { if (pendingUrl) URL.revokeObjectURL(pendingUrl); }, [pendingUrl]);

  const confirmAvatar = async () => {
    if (!pendingFile) return;
    setUploadingAvatar(true);
    try {
      const res = await userApi.uploadAvatar(pendingFile, profile?.id);
      if (res.linked) {
        notifications.show({ color: 'green', message: 'Foto profilo aggiornata' });
      } else {
        // Senza riga UserProfile non c'è dove agganciare l'immagine: dirlo, invece di
        // far credere che sia andata a buon fine.
        notifications.show({
          color: 'orange',
          message: 'File caricato, ma il profilo non esiste ancora: salva prima i dati del profilo.',
        });
      }
      clearPending();
      await qc.invalidateQueries({ queryKey: ['me-with-profile', userId] });
    } catch (err) {
      notifyError(err, 'Upload fallito');
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (isLoading) return <PageLoader label="Carico il profilo…" />;

  return (
    <Stack>
      <Title order={2}>Profilo</Title>

      <Card withBorder padding="lg" radius="lg">
        <Group align="flex-start" wrap="wrap" gap="lg">
          <div style={{ position: 'relative' }}>
            <Avatar size={80} radius="xl" src={pendingUrl ?? avatarQ.data ?? undefined}>
              {(profile?.firstName?.[0] ?? me?.firstName?.[0] ?? me?.email?.[0] ?? 'U').toUpperCase()}
            </Avatar>
            {avatarQ.isError && <QueryErrorAlert error={avatarQ.error} onRetry={() => { void avatarQ.refetch(); }} />}
      {avatarQ.isLoading && (
              <Center style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(255,255,255,0.7)' }}>
                <Loader size="xs" />
              </Center>
            )}
          </div>

          <Stack gap={8} style={{ flex: '1 1 260px', minWidth: 0 }}>
            {pendingFile ? (
              <>
                <Text size="sm" fw={600}>Confermi questa foto profilo?</Text>
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {pendingFile.name} · {(pendingFile.size / 1024).toFixed(0)} KB
                </Text>
                <Group gap="sm" wrap="wrap">
                  <Button
                    leftSection={<IconCheck size={15} />}
                    loading={uploadingAvatar}
                    onClick={() => void confirmAvatar()}
                  >
                    Conferma e carica
                  </Button>
                  <Button variant="default" disabled={uploadingAvatar} onClick={clearPending}>
                    Annulla
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Text size="sm" fw={600}>Foto profilo</Text>
                <Text size="xs" c="dimmed">PNG, JPEG o WebP. Ti chiediamo conferma prima di caricarla.</Text>
                {/*
                  Da telefono le due strade sono **due pulsanti**, non una sola voce.
                  Il selettore file di sistema, sotto la WebView, apre la galleria: la fotocamera
                  o non la offre o la nasconde in fondo a un menu, e la foto profilo è proprio
                  la cosa che si vuole scattare lì per lì.

                  `MaePhotoInput` tiene le due strade: dentro l'app chiede al plugin nativo
                  (permessi come si deve, immagine già ridotta), sul web ricade sull'`<input>`
                  di sempre — con `capture` sullo scatto. Comprime anche: uno scatto da telefono
                  recente pesa 4–12 MB contro un tetto di 11 MiB, quindi prima il caricamento
                  poteva fallire proprio sul dispositivo dove serviva.
                */}
                <Group gap={8} wrap="wrap">
                  {withCamera && (
                    <MaePhotoInput
                      source="fotocamera"
                      accept="image/png,image/jpeg,image/webp"
                      onFiles={(files) => pickAvatar(files[0] ?? null)}
                    >
                      {({ open, busy }) => (
                        <Button variant="light" leftSection={<IconCamera size={16} />} loading={busy} onClick={open}>
                          Scatta una foto
                        </Button>
                      )}
                    </MaePhotoInput>
                  )}
                  <MaePhotoInput
                    source="galleria"
                    accept="image/png,image/jpeg,image/webp"
                    onFiles={(files) => pickAvatar(files[0] ?? null)}
                  >
                    {({ open: apri, busy: occupato }) => (
                      <Button variant="light" leftSection={<IconPhoto size={16} />} loading={occupato} onClick={apri}>
                        {withCamera ? 'Scegli dalla galleria' : 'Cambia foto'}
                      </Button>
                    )}
                  </MaePhotoInput>
                </Group>
              </>
            )}
          </Stack>
        </Group>
      </Card>

      <Card withBorder padding="lg" radius="lg">
        <Title order={4} mb="sm">Account</Title>
        <form onSubmit={accountForm.onSubmit((v) => void saveAccount.mutate(v))}>
          <Stack>
            <TextInput label="Email" disabled {...accountForm.getInputProps('email')} />
            <TextInput label="Telefono account" {...accountForm.getInputProps('phoneNumber')} />
            <Group justify="flex-end">
              <Button type="submit" variant="light" loading={saveAccount.isPending}>Salva account</Button>
            </Group>
          </Stack>
        </form>
      </Card>

      <Card withBorder padding="lg" radius="lg">
        <Title order={4} mb="sm">Anagrafica</Title>
        {!profile && (
          <Text size="sm" c="orange" mb="sm">Il tuo profilo non e&apos; ancora stato creato: compila e salva.</Text>
        )}
        <form onSubmit={profileForm.onSubmit((v) => void saveProfile.mutate(v))}>
          <Stack>
            <Group grow>
              <TextInput label="Nome" {...profileForm.getInputProps('firstName')} />
              <TextInput label="Cognome" {...profileForm.getInputProps('lastName')} />
            </Group>
            <Group grow>
              <TextInput label="Nickname" {...profileForm.getInputProps('nickName')} />
              <Select
                label="Sesso"
                data={[{ value: 'M', label: 'M' }, { value: 'F', label: 'F' }, { value: 'X', label: 'Altro' }]}
                clearable
                {...profileForm.getInputProps('sex')}
              />
            </Group>
            <Group grow>
              <TextInput label="Codice fiscale" {...profileForm.getInputProps('taxId')} />
              <TextInput label="Data di nascita" type="date" {...profileForm.getInputProps('birthDate')} />
            </Group>
            <Group grow>
              <TextInput label="Telefono fisso" {...profileForm.getInputProps('fixedPhone')} />
              <TextInput label="Cellulare" {...profileForm.getInputProps('mobilePhone')} />
            </Group>
            <TextInput label="Email di contatto" {...profileForm.getInputProps('contactEmail')} />

            <Title order={5} mt="sm">Residenza</Title>
            <Group grow>
              <TextInput label="Indirizzo" {...profileForm.getInputProps('residenceAddress')} />
              <TextInput label="Civico" maw={120} {...profileForm.getInputProps('residenceHouseNumber')} />
            </Group>
            <Group grow>
              <TextInput label="Citta'" {...profileForm.getInputProps('residenceCity')} />
              <TextInput label="Provincia" {...profileForm.getInputProps('residenceProvince')} />
              <TextInput label="CAP" maw={140} {...profileForm.getInputProps('residenceZIP')} />
            </Group>
            <TextInput label="Stato" {...profileForm.getInputProps('residenceState')} />

            <Title order={5} mt="sm">Altro</Title>
            <Group grow>
              <TextInput label="Occupazione" {...profileForm.getInputProps('occupation')} />
              <Select
                label="Lingua"
                data={[{ value: 'it', label: 'Italiano' }, { value: 'en', label: 'English' }]}
                {...profileForm.getInputProps('userLang')}
              />
            </Group>
            <Textarea label="Note" autosize minRows={2} {...profileForm.getInputProps('description')} />

            <Group justify="flex-end">
              <Button type="submit" loading={saveProfile.isPending}>
                {profile ? 'Salva profilo' : 'Crea profilo'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}
